import React, { useCallback } from "react";
import { shallowEqual } from "react-redux";
import groupBy from "lodash/groupBy";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Swatch from "../../../../../../shared-components/css-modules/toyota/atoms/Swatch";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import baseSectionStyles from "../SectionWrapper.module.scss";
import styles from "./Colour.module.scss";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import CarDataConsolidator from "../../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { setColour } from "../../../../../redux/slices/CarConfigBasketSlice";
import Price from "../../../common/price/Price";
import { trackColorSelect } from "../../../../../utils/tracking";
import { AvailableInOtherGradeModalType, CarConfigConfigureSection } from "../../../../../utils/types/SectionTypes";
import type { SectionWrapperPropagationProps } from "../SectionWrapper";
import SectionWrapper from "../SectionWrapper";
import type { ColourType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import { getColourPriceRange } from "../../../../../utils/colourUtils";
import Collapse from "../../../../../../shared-components/css-modules/toyota/molecules/Collapse";
import useAphModal from "../../../../../hooks/useAphModal";
import { MODAL_OTHER_GRADE } from "../../../../../utils/modalConstants";
import { scrollToTarget } from "../../../../../../../../common-deprecated/utils/scrollUtils";
import { groupColoursBasedOnPack } from "../../../../../utils/packUtils";
import ColourSkeleton from "./ColourSkeleton";
import { getCarDBAssetUrl } from "../../../../../../../shared-logic/utils/imageUtils";
import { CarConfigLabel, useCarConfigLabel } from "../../../../../utils/labelUtils";
import SliderWrapper from "../../../common/spin/sliderWrapper/SliderWrapper";
import Badge from "../../../../../../shared-components/css-modules/toyota/atoms/Badge";
import { setActiveSpin, SpinnerType } from "../../../../../redux/slices/CarConfigSpinNavigationSlice";

const Colour: React.FC<SectionWrapperPropagationProps> = ({ sectionRefs }) => {
    const dispatch = useCarConfigDispatch();

    const colours = useCarConfigSelector(
        (state) => CarDataConsolidator.getColours(state.items.colours, state.basket),
        shallowEqual,
    );
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const coloursInOtherGrades = useCarConfigSelector((state) => state.availableInOtherGrades.availableColours);
    const activeColour = useCarConfigSelector((state) => state.basket.colour);
    const activeSpinner = useCarConfigSelector((state) => state.carConfigSpinNavigation.activeSpin);
    const packs = useCarConfigSelector((state) => state.basket.packs);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const itemsAreInitialized = useCarConfigSelector((state) => state.items.initialized);

    const carConfigBuyNow = useCarConfigLabel("carConfigAvailableInOtherGradesSummaryTitle");

    const coloursInGroups = groupColoursBasedOnPack(groupBy(colours, "type"), packs);
    const coloursInOtherGradesGroups = groupBy(coloursInOtherGrades, "type");
    const navigationLabel = useCarConfigLabel("ariaColourNavigation");

    const { showModalHelper } = useAphModal();

    const onColourClick = useCallback(
        (colour: ColourType) => () => {
            trackColorSelect();
            dispatch(setColour({ colour }));
            if (activeSpinner !== SpinnerType.Car) {
                dispatch(setActiveSpin(SpinnerType.Car));
            }
        },
        [dispatch, activeSpinner],
    );

    return (
        <SectionWrapper
            sectionId={CarConfigConfigureSection.Colour}
            sectionRef={sectionRefs.get(CarConfigConfigureSection.Colour)}
        >
            <Text tag="h2" variant="apheleia-h2" spacing="spacing-4">
                <CarConfigLabel label="carConfigColour" />
            </Text>
            <SliderWrapper />
            <nav className={baseSectionStyles["group-wrapper"]} aria-label={navigationLabel}>
                <ColourSkeleton loading={!itemsAreInitialized}>
                    {Object.entries(coloursInGroups).map(([groupPackId, groupPacks]) => {
                        return (
                            <div
                                key={groupPackId}
                                className={groupPackId !== "standard" ? baseSectionStyles["pack-group-option"] : ""}
                            >
                                {groupPackId !== "standard" && (
                                    <div className={baseSectionStyles["pack-group-option-header"]}>
                                        <CarConfigLabel
                                            label="carConfigPartOfPack"
                                            params={{ pack: packs.find((pack) => pack.shortId === groupPackId)?.name }}
                                        />
                                        <Button
                                            className={baseSectionStyles["pack-group-button"]}
                                            variant="btn-text"
                                            onClick={() => scrollToTarget(CarConfigConfigureSection.Packs)}
                                        >
                                            <CarConfigLabel label="carConfigViewPack" />
                                        </Button>
                                    </div>
                                )}

                                {Object.entries(groupPacks).map(([group, groupColours]) => {
                                    const [lowPrice, highPrice] = getColourPriceRange(groupColours);

                                    const selectedItemInGroup = groupColours.find(({ id }) => activeColour.id === id);

                                    let renderedPrice;
                                    if ((lowPrice.price?.discount ?? 0) === (highPrice.price?.discount ?? 0)) {
                                        renderedPrice = (
                                            <Price
                                                price={lowPrice.price}
                                                forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                                                showZeroPrice
                                                showDiscountHorizontal
                                            />
                                        );
                                    } else {
                                        renderedPrice = (
                                            <>
                                                <Price
                                                    price={lowPrice.price}
                                                    forceCashPrice={
                                                        !showAccessoriesRate(commonSettings, carConfigSettings)
                                                    }
                                                    showZeroPrice
                                                    showDiscountHorizontal
                                                />{" "}
                                                -{" "}
                                                <Price
                                                    price={highPrice.price}
                                                    forceCashPrice={
                                                        !showAccessoriesRate(commonSettings, carConfigSettings)
                                                    }
                                                    showZeroPrice
                                                    showDiscountHorizontal
                                                />
                                            </>
                                        );
                                    }

                                    return (
                                        <div
                                            className={`${styles.group} ${
                                                groupPackId === "standard" ? styles["not-from-pack"] : ""
                                            }`}
                                            key={group}
                                        >
                                            {financeOption !== "monthly" && (
                                                <Text
                                                    data-testid="renderedPrice"
                                                    tag="p"
                                                    variant="apheleia-body"
                                                    color="grey-7"
                                                >
                                                    {renderedPrice}
                                                </Text>
                                            )}

                                            <Text tag="h3" variant="apheleia-h5" className={styles["group-title"]}>
                                                <span>
                                                    {group}
                                                    {selectedItemInGroup ? <> - {selectedItemInGroup.name}</> : ""}
                                                </span>
                                                {selectedItemInGroup ? (
                                                    <Badge variant="price">
                                                        <Price
                                                            price={{
                                                                ...activeColour.price,
                                                                discount: selectedItemInGroup.price.discount ?? 0,
                                                            }}
                                                            forceCashPrice={
                                                                !showAccessoriesRate(commonSettings, carConfigSettings)
                                                            }
                                                            showZeroPrice
                                                            showDiscountHorizontal
                                                        />
                                                    </Badge>
                                                ) : (
                                                    ""
                                                )}
                                            </Text>
                                            <div className={styles.colours}>
                                                {/*Note, swatch should be wrapped in div due to safari not playing nicely with the animation delay custom property on hover*/}
                                                {groupColours.map((colour, index) => (
                                                    <div
                                                        className={styles.swatch}
                                                        key={colour.id}
                                                        style={{
                                                            "--or-animation-delay": `${(index + 1) * 100 + 4}`,
                                                        }}
                                                    >
                                                        <Button
                                                            variant="swatch"
                                                            aria-pressed={activeColour.id === colour.id}
                                                            aria-label={colour.name}
                                                            onClick={onColourClick(colour)}
                                                        >
                                                            <Swatch
                                                                fill={`#${
                                                                    colour.rgb.substr(0, 2) === "0x"
                                                                        ? colour.rgb.substr(2)
                                                                        : colour.rgb
                                                                }`}
                                                                active={activeColour.id === colour.id}
                                                                image={getCarDBAssetUrl(
                                                                    colour.image,
                                                                    commonSettings.compressedCcisPath,
                                                                    128,
                                                                    128,
                                                                )}
                                                            />
                                                            <span className={a11y["sr-only"]}>{colour.name}</span>
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </ColourSkeleton>
                {Object.entries(coloursInOtherGradesGroups).length > 0 && (
                    <Collapse summary={carConfigBuyNow} className={styles.other}>
                        {Object.entries(coloursInOtherGradesGroups).map(([group, groupColours]) => {
                            return (
                                /* Disable because this is only a prevention and not an actual click for the user*/
                                /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
                                <div
                                    onClick={(event) => {
                                        event.stopPropagation();
                                    }}
                                    className={clsx(styles.group, styles["not-from-pack"])}
                                    key={group}
                                >
                                    <Text tag="h3" variant="apheleia-price">
                                        {group}
                                    </Text>
                                    <div className={styles.colours}>
                                        {groupColours.map((colour) => (
                                            <div className={styles.swatch} key={colour.id}>
                                                <Button
                                                    variant="swatch"
                                                    onClick={() =>
                                                        showModalHelper(MODAL_OTHER_GRADE, {
                                                            availableInOtherGradesProps: {
                                                                section: AvailableInOtherGradeModalType.Colour,
                                                                colour,
                                                            },
                                                        })
                                                    }
                                                >
                                                    <Swatch
                                                        active={activeColour.id === colour.id}
                                                        image={getCarDBAssetUrl(
                                                            colour.image,
                                                            commonSettings.compressedCcisPath,
                                                            128,
                                                            128,
                                                        )}
                                                    />
                                                    <span className={a11y["sr-only"]}>{colour.name}</span>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </Collapse>
                )}
            </nav>
        </SectionWrapper>
    );
};

export default Colour;
